import { t } from '@hefe/i18n';
import { ClubCrudApi, ModalsSlice } from '@hefe/store';
import { Modal } from 'antd';
import { useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';
import CustomTable from '../../../core/components/table/custom-table.component';
import {
  ChildRelationConfig,
  getFormFieldsWithValues,
} from '../../models/club-crud.model';

interface ClubCrudChildTableProps<C extends { id: number }> {
  /** Parent resource name (e.g., 'partners') */
  parentResource: string;
  /** Parent item ID */
  parentId: number;
  /** Child relation configuration */
  config: ChildRelationConfig<C>;
}

/**
 * Child relation table component for nested CRUD
 * Displays a table of child items with add/edit/delete functionality
 */
export default function ClubCrudChildTable<C extends { id: number }>({
  parentResource,
  parentId,
  config,
}: ClubCrudChildTableProps<C>) {
  const dispatch = useAppDispatch();

  // RTK Query hooks for child relations
  const {
    data: items = [],
    isLoading,
    isFetching,
  } = ClubCrudApi.useFetchChildAllQuery({
    resource: parentResource,
    parentId,
    relation: config.relation,
  });

  const [createChild, { isLoading: isCreating }] =
    ClubCrudApi.useCreateChildMutation();
  const [updateChild, { isLoading: isUpdating }] =
    ClubCrudApi.useUpdateChildMutation();
  const [deleteChild, { isLoading: isDeleting }] =
    ClubCrudApi.useDeleteChildMutation();
  const [deleteMultiple, { isLoading: isDeletingMultiple }] =
    ClubCrudApi.useDeleteChildMultipleMutation();

  // Local state for edit modal
  const [selectedItem, setSelectedItem] = useState<C | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formId = `child-crud-form-${parentResource}-${config.relation}`;
  const tableName = `${parentResource}-${parentId}-${config.relation}`;

  // Handle row click to open edit modal
  const onRowClick = (item: C) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  // Handle create submit
  const handleCreateSubmit = async (values: Partial<C>) => {
    try {
      await createChild({
        resource: parentResource,
        parentId,
        relation: config.relation,
        data: values,
      }).unwrap();
      dispatch(ModalsSlice.modalsActions.closeModal());
    } catch (error) {
      // Error handling is done by RTK Query middleware
    }
  };

  // Handle edit submit
  const handleEditSubmit = async (values: Partial<C>) => {
    if (!selectedItem) return;
    try {
      await updateChild({
        resource: parentResource,
        parentId,
        relation: config.relation,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: { ...values, id: selectedItem.id } as any,
      }).unwrap();
      handleModalClose();
    } catch (error) {
      // Error handling is done by RTK Query middleware
    }
  };

  // Handle delete action
  const handleDelete = async (ids: number[], resetKeys?: () => void) => {
    try {
      if (config.multiDelete && ids.length > 1) {
        await deleteMultiple({
          resource: parentResource,
          parentId,
          relation: config.relation,
          ids,
        }).unwrap();
      } else {
        await deleteChild({
          resource: parentResource,
          parentId,
          relation: config.relation,
          childId: ids[0],
        }).unwrap();
      }
      resetKeys?.();
    } catch (error) {
      // Error handling is done by RTK Query middleware
    }
  };

  // Get form fields with values if editing
  const getEditFormFields = () => {
    const baseFields = config.editFormFields ?? config.formFields;
    if (selectedItem) {
      return getFormFieldsWithValues(baseFields, selectedItem);
    }
    return baseFields;
  };

  // Get columns - use getColumns if provided for parent context, otherwise use static columns
  const columns = config.getColumns
    ? config.getColumns({ parentResource, parentId, relation: config.relation })
    : config.columns;

  return (
    <div className="club-crud-child-table">
      <CustomTable
        rowKey="id"
        name={tableName}
        columns={columns}
        dataSource={items as unknown as C[]}
        loading={isLoading || isFetching}
        onRowClick={onRowClick}
        hideColumnSelector
        addForm={{
          title: t(`${config.i18nKey}.add`),
          formId: `${formId}-create`,
          component: (
            <CustomForm
              formId={`${formId}-create`}
              fields={config.formFields}
              onSubmit={handleCreateSubmit}
              loading={isCreating}
            />
          ),
        }}
        deleteAction={{
          action: handleDelete,
          multiDelete: config.multiDelete,
          loading: isDeleting || isDeletingMultiple,
        }}
      />

      {/* Edit Modal */}
      <Modal
        title={
          config.editTitleKey && selectedItem
            ? String(selectedItem[config.editTitleKey])
            : t(`${config.relation}.edit`)
        }
        open={isModalOpen}
        onOk={() => {
          const form = document.getElementById(
            `${formId}-edit`
          ) as HTMLFormElement;
          form?.requestSubmit();
        }}
        onCancel={handleModalClose}
        confirmLoading={isUpdating}
      >
        {selectedItem && (
          <CustomForm
            formId={`${formId}-edit`}
            fields={getEditFormFields()}
            onSubmit={handleEditSubmit}
            loading={isUpdating}
          />
        )}
      </Modal>
    </div>
  );
}
